import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';

interface RosterUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
}
interface RosterModule {
  id: string;
  slug: string;
  title: string;
  is_required: boolean;
}
interface RosterRow {
  user_id: string;
  module_id: string;
  best_quiz_score: number | null;
  quiz_passed: boolean;
  completed_lessons: number;
  total_lessons: number;
  is_complete: boolean;
}
interface RosterCert {
  user_sub: string;
  certificate_ref: string;
  earned_at: string;
}
interface Roster {
  users: RosterUser[];
  modules: RosterModule[];
  rows: RosterRow[];
  certificates: RosterCert[];
}

interface ExternalRosterRow {
  enrollment_id: string;
  module_id: string;
  module_title: string;
  contact_profile_id: string;
  contact_name: string | null;
  organization_name: string | null;
  enrolled_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_accessed_at: string | null;
  done_lessons: number;
  total_lessons: number;
  best_quiz_score: number | null;
  certificate_ref: string | null;
}

export function AdminTrainingRosterPage(): JSX.Element {
  const [tab, setTab] = useState<'internal' | 'external'>('internal');
  const q = useQuery({
    queryKey: ['admin', 'training', 'roster'],
    queryFn: () => api.get<Roster>('/v1/admin/training/roster'),
    enabled: tab === 'internal',
  });
  const extQ = useQuery({
    queryKey: ['admin', 'training', 'external-roster'],
    queryFn: () => api.get<{ rows: ExternalRosterRow[] }>('/v1/admin/training/external-roster'),
    enabled: tab === 'external',
  });

  const tabsHeader = (
    <div className="flex gap-2">
      <Button
        type="button"
        variant={tab === 'internal' ? 'primary' : 'secondary'}
        onClick={() => setTab('internal')}
      >
        Internal staff
      </Button>
      <Button
        type="button"
        variant={tab === 'external' ? 'primary' : 'secondary'}
        onClick={() => setTab('external')}
      >
        Customer-facing
      </Button>
    </div>
  );

  if (tab === 'external') {
    const rows = extQ.data?.rows ?? [];
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <Link to="/training" className="text-sm text-ink-muted hover:text-ink">← Learner view</Link>
            <h1 className="text-2xl font-semibold mt-1">Training roster</h1>
            <p className="text-sm text-ink-muted">External learners enrolled in customer-facing modules.</p>
          </div>
          {tabsHeader}
        </div>
        <Card>
          <CardHeader><CardTitle>Customer-facing enrollments</CardTitle></CardHeader>
          <CardBody className="overflow-x-auto">
            {extQ.isLoading && <p className="text-ink-muted">Loading…</p>}
            {extQ.isError && <p className="text-danger">Failed to load.</p>}
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="py-2 pr-3">Learner</th>
                  <th className="py-2 pr-3">Module</th>
                  <th className="py-2 pr-3">Progress</th>
                  <th className="py-2 pr-3">Quiz best</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Last access</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-center text-ink-muted">
                    No external enrollments yet.
                  </td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.enrollment_id} className="border-b border-border align-top">
                    <td className="py-2 pr-3">
                      <div className="font-medium">{r.contact_name ?? '(unnamed)'}</div>
                      <div className="text-xs text-ink-muted">{r.organization_name ?? ''}</div>
                    </td>
                    <td className="py-2 pr-3">{r.module_title}</td>
                    <td className="py-2 pr-3 text-xs">{r.done_lessons} / {r.total_lessons}</td>
                    <td className="py-2 pr-3 text-xs">
                      {r.best_quiz_score === null ? '—' : `${Math.round(r.best_quiz_score)}%`}
                    </td>
                    <td className="py-2 pr-3">
                      {r.revoked_at ? (
                        <Badge tone="neutral">Revoked</Badge>
                      ) : r.certificate_ref ? (
                        <Badge tone="success">Certified</Badge>
                      ) : new Date(r.expires_at).getTime() < Date.now() ? (
                        <Badge tone="warning">Expired</Badge>
                      ) : (
                        <Badge tone="neutral">Active</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs text-ink-muted">
                      {r.last_accessed_at
                        ? new Date(r.last_accessed_at).toLocaleDateString()
                        : 'never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (q.isLoading) return <p className="text-ink-muted">Loading roster…</p>;
  if (q.isError || !q.data) return <p className="text-danger">Failed to load roster.</p>;

  const { users, modules, rows, certificates } = q.data;
  const byKey = new Map<string, RosterRow>();
  for (const r of rows) byKey.set(`${r.user_id}::${r.module_id}`, r);
  const certBy = new Map(certificates.map((c) => [c.user_sub, c]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Link to="/training" className="text-sm text-ink-muted hover:text-ink">← Learner view</Link>
          <h1 className="text-2xl font-semibold mt-1">Training roster</h1>
          <p className="text-sm text-ink-muted">
            Per-user completion and best quiz score for every active training module in this tenant.
          </p>
        </div>
        {tabsHeader}
      </div>

      <Card>
        <CardHeader><CardTitle>Completion matrix</CardTitle></CardHeader>
        <CardBody className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-2 pr-4">User</th>
                {modules.map((m) => (
                  <th key={m.id} className="py-2 pr-4 align-bottom">
                    <div className="font-medium">{m.title}</div>
                    {m.is_required && (
                      <div className="text-[10px] uppercase tracking-wide text-ink-muted">Required</div>
                    )}
                  </th>
                ))}
                <th className="py-2 pr-4">Certificate</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={modules.length + 2} className="py-4 text-ink-muted text-center">
                    No active users in this tenant.
                  </td>
                </tr>
              )}
              {users.map((u) => {
                const cert = certBy.get(u.id);
                return (
                  <tr key={u.id} className="border-b border-border align-top">
                    <td className="py-2 pr-4">
                      <div className="font-medium">{u.name ?? u.email}</div>
                      <div className="text-xs text-ink-muted">{u.email}</div>
                      <div className="text-[10px] uppercase tracking-wide text-ink-muted">{u.role}</div>
                    </td>
                    {modules.map((m) => {
                      const r = byKey.get(`${u.id}::${m.id}`);
                      const lessonStr = r ? `${r.completed_lessons}/${r.total_lessons} lessons` : '—';
                      const scoreStr =
                        r && r.best_quiz_score !== null ? `${r.best_quiz_score}% best` : 'no attempts';
                      return (
                        <td key={m.id} className="py-2 pr-4">
                          {r?.is_complete ? (
                            <Badge tone="success">Complete</Badge>
                          ) : r?.quiz_passed ? (
                            <Badge tone="warning">Quiz passed</Badge>
                          ) : (
                            <Badge tone="neutral">In progress</Badge>
                          )}
                          <div className="text-xs text-ink-muted mt-1">{lessonStr}</div>
                          <div className="text-xs text-ink-muted">{scoreStr}</div>
                        </td>
                      );
                    })}
                    <td className="py-2 pr-4">
                      {cert ? (
                        <div>
                          <Badge tone="success">Certified</Badge>
                          <div className="text-xs text-ink-muted mt-1">{cert.certificate_ref}</div>
                          <div className="text-xs text-ink-muted">
                            {new Date(cert.earned_at).toLocaleDateString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
